import React, {
  useState, useMemo, useRef, useCallback, Fragment, ReactElement, RefObject,
} from 'react';
import { useNavigate } from 'react-router';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import ContextMenu from '../../../../components/context-menu/context-menu.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import NoContentBlock from '../../../../components/no-content-block/no-content-block.component';
import TextNoWrapBlock from '../../../../components/text-no-wrap-block/text-no-wrap-block.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import VisibleCell from '../../../../components/visible-cell/visible-cell.component';
import List from '../../../../components/list/list.component';
import ListItem from '../../../../components/list-item/list-item.component';
import ListItemText from '../../../../components/list-item-text/list-item-text.component';
import ConnectorLinkBlock from '../../../../components/connector-link-block/connector-link-block.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import ContentListContainerOffset
  from '../../../app/common/content-list-container-offset/content-list-container-offset.component';
import SettingsPortfolioRow from './settings-portfolio-row/settings-portfolio-row.component';
import EditIcon from '../../../../icons/edit-icon';
import DeleteItemIcon from '../../../../icons/delete-item-icon';
import MoreIcon from '../../../../icons/more-icon';
import ArrowLinkIcon from '../../../../icons/arrow-link-icon';
import FormatService from '../../../../services/format-service';
import ScreenService from '../../../../services/screen-service';
import useArrowNavigation from '../../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../../hooks/use-screen-small';
import { ROUTE_PORTFOLIO } from '../../../../constants/system/routes';
import { PORTFOLIO_EDIT, PORTFOLIO_DELETE } from '../../../../constants/entity/permissions';
import { QUERY_PARAM_EDIT_MODE } from '../../../../constants/system/query-params';
import { DATA_PORTFOLIO_ID } from '../../../../constants/system/data-attributes';
import { MARKER_COLOR_RED } from '../../../../constants/entity/marker-colors';

const SettingsPortfolioList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    portfolioActiveIds,
    portfolioMarkerColorList,
    portfolioGroupList,
    portfolioList,
    portfolioGroupData,
    formError,
    inlineSavePortfolioId,
    onCancelForm,
    onSubmitForm,
    onInlineSave,
    onInlineCancel,
    onDealConnectEmailClick,
    onPortfolioDeleteClick,
    onInlineEditModeButtonClick,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const isSmallScreen: boolean = useScreenSmall();
  const portfolioMarkerColorDefault: PortfolioMarkerColor | null = portfolioMarkerColorList
  && portfolioMarkerColorList.length ? portfolioMarkerColorList.find((portfolioItem: Portfolio): boolean => (
    portfolioItem.name === MARKER_COLOR_RED
  )) : null;

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);

  const portfolioEditPermissions: string[] = useMemo((): string[] => [PORTFOLIO_EDIT], []);

  const onRowClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    portfolio: Portfolio,
    isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      if (isEditMode && isListView) {
        const queryParams: LocationQueryParams = {
          [QUERY_PARAM_EDIT_MODE]: isEditMode,
        };

        navigate({
          pathname: `${ROUTE_PORTFOLIO}/${portfolio.id}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      } else if (!isEditMode) {
        navigate({
          pathname: `${ROUTE_PORTFOLIO}/${portfolio.id}`,
        });
      }
    }
  }, [isEditMode]);

  const dealConnectEmailClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, portfolioId: number,
  ): void => {
    if (onDealConnectEmailClick) {
      onDealConnectEmailClick(portfolioId);
    }

    event.stopPropagation();
  }, []);

  const checkIsPortfolioActive: Function = useCallback((portfolioId: number): boolean => (
    portfolioActiveIds ? portfolioActiveIds.includes(portfolioId) : false
  ), [portfolioActiveIds]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const portfolioDeleteClick: Function = useCallback((portfolioId: number): void => {
    if (onPortfolioDeleteClick) {
      onPortfolioDeleteClick(portfolioId);
    }
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: PORTFOLIO_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, portfolioId: number): void => {
        if (portfolioId) {
          portfolioDeleteClick(portfolioId);
        }
      },
    },
  ], []);

  const tablePortfolioListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      title: 'Name',
      isRequired: isEditMode,
      component: (portfolio: Portfolio): ReactElement => (
        <TableCell isText>
          {portfolio.name}
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'markerColor',
      title: 'Marker Color',
      isRequired: isEditMode,
      component: (portfolio: Portfolio): ReactElement => (
        <TableCell isText>
          {
            portfolio.markerColor ? portfolio.markerColor.title
              : (portfolioMarkerColorDefault && portfolioMarkerColorDefault.title)
          }
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'capitalAllocated',
      title: 'Capital Allocated',
      isNumber: true,
      component: (portfolio: Portfolio): ReactElement => (
        <TableCell isNumber={!isEditMode}>
          {portfolio.capitalAllocated}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'portfolioGroup',
      title: 'Portfolio Group',
      isEditModeOnly: true,
      isRequired: isEditMode,
      component: (): ReactElement => (
        <TableCell>
          {portfolioGroupData.name}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'isHidden',
      title: 'Is Hidden',
      component: (portfolio: Portfolio): ReactElement => (
        <TableCell>
          {FormatService.formatBooleanValue(portfolio.isHidden)}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'dealConnectorEmail',
      title: 'Deal Connector Email',
      component: (portfolio: Portfolio): ReactElement => (
        <TableCell isText>
          {
            isEditMode && (
              <TextNoWrapBlock>
                {portfolio.dealConnectorEmail}
              </TextNoWrapBlock>
            )
          }
          {
            !isEditMode && portfolio.dealConnectorEmail && (
              <ConnectorLinkBlock
                isEmail
                isConnected={portfolio.isDealConnectorEnabled}
                link={portfolio.dealConnectorEmail}
                onConnectClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  dealConnectEmailClick(event, portfolio.id);
                }}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'actions',
      title: 'Actions',
      isHeaderNameHidden: true,
      isSticky: true,
      isStickyRight: true,
      component: (portfolio: Portfolio): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            (!isEditMode || !checkIsPortfolioActive(portfolio.id)) && (
              <CheckAccess permissions={portfolioEditPermissions}>
                <ActionButton
                  withNoPadding
                  isHidden={!!isSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, portfolio.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
          {
            !isEditMode && (
              <CheckAccess
                permissions={actionMenuItems
                  && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => (
                    actionMenuItem.permission
                  ))}
              >
                <ActionButton
                  withPopup
                  withNoPadding
                  withFixedSize
                  isHidden={!!isSaving}
                  onClick={openActionMenu}
                  {...{
                    [DATA_PORTFOLIO_ID]: portfolio.id,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
    },
  ], [isEditMode, menuAnchor, isSaving, portfolioGroupData, portfolioActiveIds]);

  useArrowNavigation(inputRefs);

  return (
    <SinglePageTab
      withNoPadding
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      {
        portfolioList && !!portfolioList.length ? (
          <Fragment>
            {
              !isSmallScreen ? (
                <ContentListContainerOffset isDefault>
                  <Table>
                    <TableHead isSubHeader withFixedCellsSize>
                      <TableRow>
                        {
                          tablePortfolioListFields.map((tableField: TableField): ReactElement => (
                            <VisibleCell isEditMode={isEditMode} key={tableField.id} tableField={tableField}>
                              <TableCell
                                isNumber={tableField.isNumber && !isEditMode}
                                isSticky={tableField.isSticky}
                                isStickyRight={tableField.isSticky && tableField.isStickyRight}
                              >
                                {
                                  !tableField.isHeaderNameHidden && (
                                    <TextBlock>
                                      {tableField.title}
                                      {tableField.isRequired && <RequiredMark />}
                                    </TextBlock>
                                  )
                                }
                              </TableCell>
                            </VisibleCell>
                          ))
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody isPlain withZebraRows withFixedCellsSize withBorderedCells>
                      {
                        portfolioList.map((portfolio: Portfolio, portfolioIndex: number): ReactElement => {
                          const isActive: boolean = checkIsPortfolioActive(portfolio.id);
                          const isInlineSaving: boolean = inlineSavePortfolioId !== null
                            ? portfolio.id === inlineSavePortfolioId : isSaving;

                          return (
                            <SettingsPortfolioRow
                              isActive={isActive}
                              isSaving={isSaving}
                              isSaveDisabled={isSaveDisabled}
                              isInlineSaving={isInlineSaving}
                              isEditMode={isEditMode}
                              key={portfolio.id}
                              portfolioItem={portfolio}
                              portfolioGroupList={portfolioGroupList}
                              portfolioMarkerColorList={portfolioMarkerColorList}
                              portfolioIndex={portfolioIndex}
                              tablePortfolioListFields={tablePortfolioListFields}
                              inputRefs={inputRefs}
                              onRowClick={onRowClick}
                              onInlineSave={onInlineSave}
                              onInlineCancel={onInlineCancel}
                            />
                          );
                        })
                      }
                    </TableBody>
                  </Table>
                </ContentListContainerOffset>
              ) : (
                <ContentListContainerOffset isDefault>
                  <List>
                    {
                      portfolioList.map((portfolio: Portfolio): ReactElement => {
                        const isActive: boolean = checkIsPortfolioActive(portfolio.id);

                        return (
                          <ListItem
                            isButton
                            isExtended
                            isActive={isActive}
                            key={portfolio.id}
                            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                              onRowClick(event, portfolio, true);
                            }}
                          >
                            <ListItemText isBlock>
                              {portfolio.name}
                            </ListItemText>
                            <ListItemText
                              isBlock
                              isBlockRight
                              subText={!isEditMode ? portfolio.capitalAllocated : (
                                <FlexContainer isEnd>
                                  <ActionButton
                                    withSmallPadding
                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                      onRowClick(event, portfolio, true);
                                    }}
                                  >
                                    <ArrowLinkIcon />
                                  </ActionButton>
                                </FlexContainer>
                              )}
                            />
                          </ListItem>
                        );
                      })
                    }
                  </List>
                </ContentListContainerOffset>
              )
            }
          </Fragment>
        ) : (
          <NoContentBlock isExtended>
            No portfolios
          </NoContentBlock>
        )
      }
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_PORTFOLIO_ID}
        onClose={closeActionMenu}
      />
    </SinglePageTab>
  );
});

export default SettingsPortfolioList;
